import { authScopeForProductSet, toCustomAuthHeader } from '@upright/common/iam/access/custom-auth';
import { ProductSetAccessToken } from '@upright/common/model/input/blanket/blanket-product-set/types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { ApiTarget } from '@upright/ui/api/common/types';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActiveProductSetForCompanyResponse,
  ProductWeightFormSubmissions,
} from '@upright/common/model/input/blanket/product-weight-form';

const FETCH_PRODUCT_WEIGHT_FORM_SUBMISSIONS = 'fetchProductWeightFormSubmissions';

async function fetchProductWeightFormSubmission(productSetId: number, secretLinkToken?: ProductSetAccessToken) {
  const url = `/input-products/${productSetId}/submissions`;

  const customAuthHeader = secretLinkToken
    ? toCustomAuthHeader(authScopeForProductSet(secretLinkToken.productSetId), secretLinkToken.signature)
    : undefined;
  return await fetchOrThrow<ProductWeightFormSubmissions>(url, 'GET', undefined, ApiTarget.SERVER, customAuthHeader);
}
export function useProductWeightFormSubmissions(
  productSetId: number | undefined,
  secretLinkToken?: ProductSetAccessToken,
) {
  return useQuery({
    queryKey: [FETCH_PRODUCT_WEIGHT_FORM_SUBMISSIONS, { productSetId }],

    queryFn: async () => {
      // The query should be enabled only if there's productSetId available
      if (!productSetId) throw Error('Unexpectedly no product set id');
      return await fetchProductWeightFormSubmission(productSetId, secretLinkToken);
    },

    enabled: !!productSetId,
  });
}
async function submitProductWeightForm(productSetId: number, secretLinkToken?: ProductSetAccessToken) {
  const url = `/input-products/${productSetId}/submit`;

  const customAuthHeader = secretLinkToken
    ? toCustomAuthHeader(authScopeForProductSet(secretLinkToken.productSetId), secretLinkToken.signature)
    : undefined;
  return await fetchOrThrow<unknown>(url, 'POST', undefined, ApiTarget.SERVER, customAuthHeader);
}

export function useSubmitProductWeightForm(
  productSetId: number | undefined,
  secretLinkToken?: ProductSetAccessToken,
): UseMutationResult<unknown, Error, void, unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // If submitting is visible to the user, we should already have a productSetId
      if (!productSetId) throw Error('Unexpectedly no product set id');
      return await submitProductWeightForm(productSetId, secretLinkToken);
    },

    onSuccess: async _newProduct => {
      await queryClient.invalidateQueries({
        queryKey: [FETCH_PRODUCT_WEIGHT_FORM_SUBMISSIONS],
      });
    },
  });
}

async function getCPProductSetForCompany(companyId: string) {
  return await fetchOrThrow<ActiveProductSetForCompanyResponse>(`/company-platform/${companyId}/product-set`, 'GET');
}

export function useGetCPProductSetForCompanyQuery(companyId: string | undefined | null) {
  return useQuery({
    queryKey: ['getCPProductSetForCompany', companyId],

    queryFn: async () => {
      if (!companyId) throw Error('Unexpectedly no companyId. Query should not be enabled.');
      return await getCPProductSetForCompany(companyId);
    },

    enabled: Boolean(companyId),
  });
}
